import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Alert,
  Badge,
  Button,
  Collapse,
  Spinner,
  Table,
  Progress,
} from "reactstrap"
import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineWarning,
  AiOutlineInfoCircle,
  AiOutlineBank,
  AiOutlineFileSearch,
  AiOutlineReload,
} from "react-icons/ai"

import { consultarNosis, organizarVariablesPorCategoria } from "api/nosisApi"

// ============================================
// Componentes auxiliares
// ============================================

const RiesgoBadge = ({ nivel }) => {
  const config = {
    BAJO: { color: "success", text: "Bajo", icon: <AiOutlineCheckCircle /> },
    MEDIO: { color: "warning", text: "Medio", icon: <AiOutlineWarning /> },
    ALTO: { color: "danger", text: "Alto", icon: <AiOutlineWarning /> },
    CRITICO: { color: "dark", text: "Crítico", icon: <AiOutlineCloseCircle /> },
    INDETERMINADO: {
      color: "secondary",
      text: "Sin datos",
      icon: <AiOutlineInfoCircle />,
    },
  }
  const cfg = config[nivel] || config.INDETERMINADO
  return (
    <Badge
      color={cfg.color}
      className="d-flex align-items-center gap-1 px-2 py-1"
    >
      {cfg.icon} {cfg.text}
    </Badge>
  )
}

const ScoreGauge = ({ score, maxScore = 999 }) => {
  if (score === null || score === undefined) {
    return (
      <div className="text-center text-muted">
        <small>Sin score</small>
      </div>
    )
  }

  const porcentaje = Math.round((score / maxScore) * 100)
  let color = "success"
  if (score < 300) color = "danger"
  else if (score < 500) color = "warning"
  else if (score < 700) color = "info"

  return (
    <div className="text-center">
      <div
        className="display-6 fw-bold"
        style={{ color: `var(--bs-${color})` }}
      >
        {score}
      </div>
      <Progress
        value={porcentaje}
        color={color}
        className="mt-2"
        style={{ height: 8 }}
      />
      <small className="text-muted">de {maxScore}</small>
    </div>
  )
}

const AlertaItem = ({ alerta }) => {
  const colorMap = {
    CRITICO: "danger",
    ALTO: "warning",
    MEDIO: "info",
    BAJO: "secondary",
  }
  const color = colorMap[alerta.tipo] || "secondary"

  return (
    <Alert color={color} className="py-2 mb-2 d-flex align-items-center">
      {alerta.tipo === "CRITICO" ? (
        <AiOutlineCloseCircle className="me-2" size={18} />
      ) : (
        <AiOutlineWarning className="me-2" size={18} />
      )}
      <div>
        <span>{alerta.mensaje}</span>
        {alerta.variable && (
          <small className="text-muted ms-2">({alerta.variable})</small>
        )}
      </div>
    </Alert>
  )
}

const VariableRow = ({ variable }) => {
  const [expanded, setExpanded] = useState(false)
  const tieneDetalle = variable.detalle && variable.detalle.length > 0

  // Soportar tanto mayúscula como minúscula
  const nombre = variable.Nombre || variable.nombre
  const descripcion = variable.Descripcion || variable.descripcion
  const valor = variable.Valor || variable.valor
  const fechaAct =
    variable.FechaAct || variable.fechaActualizacion || variable.fechaAct
  const detalle = variable.Detalle || variable.detalle

  return (
    <>
      <tr
        className={tieneDetalle ? "cursor-pointer" : ""}
        onClick={() => tieneDetalle && setExpanded(!expanded)}
      >
        <td>
          <code className="small">{nombre}</code>
        </td>
        <td>
          <small>{descripcion || "-"}</small>
        </td>
        <td className="fw-bold">{valor || "-"}</td>
        <td>
          <small className="text-muted">{fechaAct || "-"}</small>
        </td>
        {tieneDetalle && (
          <td>
            <Button color="link" size="sm" className="p-0">
              {expanded ? "▲" : "▼"}
            </Button>
          </td>
        )}
      </tr>
      {expanded && tieneDetalle && (
        <tr>
          <td colSpan={5} className="bg-light">
            <small className="text-muted">{detalle}</small>
          </td>
        </tr>
      )}
    </>
  )
}

// ============================================
// Componente principal: SeccionNosis
// ============================================

const SeccionNosis = ({
  cuit,
  denominacion,
  mostrarCompleto = true,
  onResultado,
  autoConsultar = true,
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [datos, setDatos] = useState(null)
  const [variablesPorCategoria, setVariablesPorCategoria] = useState(null)
  const [mostrarDetalle, setMostrarDetalle] = useState(false)

  const consultaRealizada = React.useRef(null)

  // Ejecutar consulta
  const ejecutarConsulta = async () => {
    if (!cuit) {
      setError("No se proporcionó CUIT")
      return
    }

    setLoading(true)
    setError(null)
    consultaRealizada.current = cuit

    try {
      // consultarNosis ya devuelve los datos procesados con el análisis incluido
      const resultado = await consultarNosis(cuit)

      if (!resultado.exito) {
        setError(resultado.error || "Error en la consulta")
        return
      }

      setDatos(resultado)

      // Organizar variables por categoría para la tabla de detalle
      if (resultado.variablesArray) {
        const categorias = organizarVariablesPorCategoria(
          resultado.variablesArray,
        )
        setVariablesPorCategoria(categorias)
      }

      // Notificar al padre
      if (onResultado) {
        onResultado({
          exitoso: true,
          nivelRiesgo: resultado.analisis?.nivelRiesgo || "INDETERMINADO",
          score: resultado.score,
          alertas: resultado.analisis?.alertas || [],
          respuestaCompleta: resultado,
        })
      }
    } catch (err) {
      console.error("Error consultando Nosis:", err)
      setError(err.message || "Error al consultar Nosis")

      if (onResultado) {
        onResultado({
          exitoso: false,
          nivelRiesgo: "INDETERMINADO",
          error: err.message,
        })
      }
    } finally {
      setLoading(false)
    }
  }

  // Auto-consultar cuando cambia el CUIT
  useEffect(() => {
    if (autoConsultar && cuit && cuit !== consultaRealizada.current) {
      ejecutarConsulta()
    }
  }, [cuit, autoConsultar])

  // Formatear moneda
  const formatearMoneda = valor => {
    if (valor === null || valor === undefined) return "-"
    return `$${valor.toLocaleString("es-AR")}`
  }

  // Loading
  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody className="text-center py-4">
          <Spinner color="primary" />
          <p className="mt-2 mb-0 text-muted">Consultando Nosis...</p>
        </CardBody>
      </Card>
    )
  }

  const nivelRiesgo = datos?.analisis?.nivelRiesgo || "INDETERMINADO"
  const alertas = datos?.analisis?.alertas || []

  return (
    <Card className="mb-3">
      <CardHeader className="bg-light d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <AiOutlineFileSearch size={20} className="me-2 text-primary" />
          <strong>Nosis - Información Crediticia</strong>
        </div>
        <div className="d-flex align-items-center gap-2">
          {datos && <RiesgoBadge nivel={nivelRiesgo} />}
          <Button
            color="light"
            size="sm"
            onClick={() => {
              consultaRealizada.current = null
              ejecutarConsulta()
            }}
            disabled={loading}
          >
            <AiOutlineReload />
          </Button>
        </div>
      </CardHeader>

      <CardBody>
        {/* Error */}
        {error && (
          <Alert color="danger" className="mb-3">
            <AiOutlineCloseCircle className="me-2" />
            {error}
          </Alert>
        )}

        {/* Sin CUIT */}
        {!cuit && !error && (
          <Alert color="info" className="mb-0">
            <AiOutlineInfoCircle className="me-2" />
            Ingrese un CUIT para consultar Nosis
          </Alert>
        )}

        {/* Resultados */}
        {datos && datos.exito && (
          <>
            {/* Resumen principal */}
            <Row className="mb-4">
              <Col md={4} className="text-center border-end">
                <small className="text-muted d-block mb-2">Score Nosis</small>
                <ScoreGauge score={datos.score} />
              </Col>
              <Col md={4} className="text-center border-end">
                <small className="text-muted d-block mb-2">
                  Nivel de Riesgo
                </small>
                <div className="mt-3">
                  <RiesgoBadge nivel={nivelRiesgo} />
                </div>
                {datos.dictamen && (
                  <small className="text-muted d-block mt-1">
                    Dictamen: {datos.dictamen}
                  </small>
                )}
              </Col>
              <Col md={4} className="text-center">
                <small className="text-muted d-block mb-2">
                  Deuda Bancaria
                </small>
                <div className="h4 mb-0">
                  {formatearMoneda(datos.montoTotal)}
                </div>
                {datos.cantidadBancos && (
                  <small className="text-muted">
                    en {datos.cantidadBancos} banco
                    {datos.cantidadBancos > 1 ? "s" : ""}
                  </small>
                )}
              </Col>
            </Row>

            {/* Info BCRA */}
            <Row className="mb-3">
              <Col md={3}>
                <small className="text-muted d-block">Situación BCRA</small>
                <Badge
                  color={datos.situacionBCRA === "1" ? "success" : "warning"}
                >
                  {datos.situacionBCRA || "-"}
                </Badge>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">
                  Perfil Cumplimiento
                </small>
                <span>{datos.perfilCumplimiento || "-"}</span>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Antigüedad BCRA</small>
                <span>
                  {datos.antiguedadBCRA ? `${datos.antiguedadBCRA} meses` : "-"}
                </span>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Es Moroso</small>
                <Badge color={datos.esMoroso ? "danger" : "success"}>
                  {datos.esMoroso ? "Sí" : "No"}
                </Badge>
              </Col>
            </Row>

            {/* Alertas */}
            {alertas.length > 0 && (
              <div className="mb-4">
                <h6 className="text-muted mb-2">
                  <AiOutlineWarning className="me-1" />
                  Alertas ({alertas.length})
                </h6>
                {alertas.map((alerta, idx) => (
                  <AlertaItem key={idx} alerta={alerta} />
                ))}
              </div>
            )}

            {/* Info de la empresa */}
            <Row className="mb-3">
              {datos.razonSocial && (
                <Col md={4}>
                  <small className="text-muted d-block">Razón Social</small>
                  <span>{datos.razonSocial}</span>
                </Col>
              )}
              {datos.tipoSocietario && (
                <Col md={4}>
                  <small className="text-muted d-block">Tipo Societario</small>
                  <span>{datos.tipoSocietario}</span>
                </Col>
              )}
              {datos.edadEmpresa && (
                <Col md={4}>
                  <small className="text-muted d-block">
                    Antigüedad Empresa
                  </small>
                  <span>{datos.edadEmpresa} años</span>
                </Col>
              )}
            </Row>

            <Row className="mb-3">
              {datos.actividadPrincipal && (
                <Col md={6}>
                  <small className="text-muted d-block">
                    Actividad Principal
                  </small>
                  <span>{datos.actividadPrincipal}</span>
                </Col>
              )}
              {datos.sectorActividad && (
                <Col md={3}>
                  <small className="text-muted d-block">Sector</small>
                  <span>{datos.sectorActividad}</span>
                </Col>
              )}
              {datos.cantidadEmpleados && (
                <Col md={3}>
                  <small className="text-muted d-block">Empleados</small>
                  <span>{datos.cantidadEmpleados}</span>
                </Col>
              )}
            </Row>

            {/* Indicadores negativos */}
            <Row className="mb-3">
              <Col md={3}>
                <small className="text-muted d-block">Juicios (12m)</small>
                <Badge color={datos.cantidadJuicios > 0 ? "danger" : "success"}>
                  {datos.cantidadJuicios ?? 0}
                </Badge>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Concursos (12m)</small>
                <Badge
                  color={datos.cantidadConcursos > 0 ? "danger" : "success"}
                >
                  {datos.cantidadConcursos ?? 0}
                </Badge>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Facturas Apócrifas</small>
                <Badge
                  color={datos.tieneFacturasApocrifas ? "danger" : "success"}
                >
                  {datos.tieneFacturasApocrifas ? "Sí" : "No"}
                </Badge>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Deudas Fiscales</small>
                <Badge color={datos.tieneDeudasFiscales ? "danger" : "success"}>
                  {datos.tieneDeudasFiscales ? "Sí" : "No"}
                </Badge>
              </Col>
            </Row>

            {/* Compliance */}
            {(datos.esSujetoObligado || datos.cantidadHomonimosLAFT > 0) && (
              <Row className="mb-3">
                <Col md={6}>
                  <small className="text-muted d-block">Sujeto Obligado</small>
                  <Badge
                    color={datos.esSujetoObligado ? "warning" : "secondary"}
                  >
                    {datos.esSujetoObligado ? "Sí" : "No"}
                  </Badge>
                </Col>
                <Col md={6}>
                  <small className="text-muted d-block">Homónimos LA/FT</small>
                  <Badge
                    color={
                      datos.cantidadHomonimosLAFT > 0 ? "warning" : "success"
                    }
                  >
                    {datos.cantidadHomonimosLAFT ?? 0}
                  </Badge>
                  {datos.linkHomonimosLAFT &&
                    datos.cantidadHomonimosLAFT > 0 && (
                      <a
                        href={datos.linkHomonimosLAFT}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-2 small"
                      >
                        Ver detalle
                      </a>
                    )}
                </Col>
              </Row>
            )}

            {/* Detalle completo (colapsable) */}
            {mostrarCompleto && variablesPorCategoria && (
              <>
                <Button
                  color="link"
                  size="sm"
                  className="p-0 mb-2"
                  onClick={() => setMostrarDetalle(!mostrarDetalle)}
                >
                  {mostrarDetalle ? "▲ Ocultar" : "▼ Ver"} todas las variables (
                  {datos.variablesArray?.length || 0})
                </Button>

                <Collapse isOpen={mostrarDetalle}>
                  {Object.entries(variablesPorCategoria).map(
                    ([key, categoria]) =>
                      categoria.variables?.length > 0 && (
                        <div className="mb-3" key={key}>
                          <h6 className="text-muted border-bottom pb-1">
                            <i className={`bx ${categoria.icon} me-1`}></i>
                            {categoria.titulo}
                          </h6>
                          <Table size="sm" className="mb-0" responsive>
                            <thead>
                              <tr>
                                <th style={{ width: "20%" }}>Variable</th>
                                <th style={{ width: "35%" }}>Descripción</th>
                                <th style={{ width: "25%" }}>Valor</th>
                                <th style={{ width: "20%" }}>Fecha Act.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {categoria.variables.map((v, i) => (
                                <VariableRow key={i} variable={v} />
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ),
                  )}
                </Collapse>
              </>
            )}

            {/* Metadatos */}
            <div className="text-muted small mt-3 pt-2 border-top">
              <span className="me-3">
                <AiOutlineInfoCircle className="me-1" />
                CUIT: {datos.cuit}
              </span>
              {datos.fechaConsulta && (
                <span>
                  Consulta:{" "}
                  {new Date(datos.fechaConsulta).toLocaleString("es-AR")}
                </span>
              )}
            </div>
          </>
        )}

        {/* Sin datos después de consulta */}
        {!loading && !error && cuit && !datos && (
          <Alert color="secondary" className="mb-0">
            <AiOutlineInfoCircle className="me-2" />
            Haga clic en recargar para consultar Nosis
          </Alert>
        )}
      </CardBody>
    </Card>
  )
}

export default SeccionNosis
